import {
  Calendar,
  Check,
  CheckCircle,
  Circle,
  Palette,
  Pencil,
} from "@tamagui/lucide-icons";
import { Timestamp } from "firebase/firestore";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { Pressable } from "react-native";
import { TouchableOpacity } from "react-native-gesture-handler";
import { Image, Text, View, XStack, YStack } from "tamagui";
import { DatePicker } from "../../../core/components/FormDataModal";
import { formatDate } from "../../../core/dayJs";
import { useUploadCover } from "../../../core/hooks/useUploadCover";
import { TagColorsAvailable } from "../../../core/tags/types";
import { DEFAULT_COVER_URL } from "../../../plans/types";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { EmojiPicker } from "../../../ui/EmojiPicker";
import { Label } from "../../../ui/Label";
import { Title, TitleTextarea } from "../../../ui/Title";
import { EditorForm } from "../../../ui/YooptaEditor";
import type { BusinessPlanModel } from "../BusinessPlanModel";

export const BusinessInfoSettingsUI = observer(
  ({ businessPlan }: { businessPlan: BusinessPlanModel }) => {
    const planInfo = businessPlan.data.planInfo;

    const [showEmojiSheet, setSetEmojiSheet] = useState(false);

    const uploadCoverButton = useUploadCover(
      (isUploading) => {
        console.info("Got is uploading", isUploading);
      },
      (coverUrl) => {
        return businessPlan.updatePlanInfo({ coverUrl, backgroundColor: null });
      },
      businessPlan.uid,
    );

    const [showColors, setShowColors] = React.useState(false);
    const [showDatePicker, setShowDatePicker] = React.useState(false);

    const icon = (
      <Text lineHeight={24} fontSize={24}>
        {planInfo.emoji}
      </Text>
    );

    let coverUrl = planInfo.coverUrl;
    if (!coverUrl && !planInfo.backgroundColor) {
      coverUrl = DEFAULT_COVER_URL;
    }

    const eventDate =
      businessPlan.data.eventInfo.date ?? Timestamp.fromDate(new Date());

    return (
      <>
        <YStack gap="$space.medium">
          <Box>
            <YStack
              gap="$space.medium"
              onPress={() => {
                setShowDatePicker(true);
              }}
              cursor="pointer"
            >
              <Label color={"$muted"}>Event date</Label>
              <Body>{formatDate(eventDate.toDate().toISOString())}</Body>
            </YStack>

            <DatePicker
              header={
                <YStack gap="$space.medium">
                  <XStack gap="$medium" alignItems="center">
                    <Calendar />
                    <Title>Set Event date</Title>
                  </XStack>
                </YStack>
              }
              value={eventDate.toDate().toISOString().split("T")[0]}
              open={showDatePicker}
              onOpenChange={(open: boolean) => {
                setShowDatePicker(open);
              }}
              sheetId="dateDuePicker"
              onSelect={(date) => {
                businessPlan.updateEventInfo({
                  date: Timestamp.fromDate(new Date(date)),
                });
                setShowDatePicker(false);
              }}
            />
          </Box>

          <Box>
            <XStack gap="$space.medium" alignItems={"center"}>
              <Label color={"$muted"}>Title</Label>
              <TitleTextarea
                icon={Pencil}
                focusOnIconPress
                defaultValue={planInfo.title ?? ""}
                submitOnBlur
                submitOnEnter
                blurOnSubmit
                onSubmitEditing={(e) => {
                  void businessPlan.updatePlanInfo({
                    title: e.nativeEvent.text,
                  });
                }}
              />
            </XStack>
          </Box>

          <Pressable onPress={() => setSetEmojiSheet(true)}>
            <Box>
              <XStack gap="$space.medium">
                <Label color={"$muted"}>Icon</Label>
                {icon}
              </XStack>
            </Box>
          </Pressable>

          <Box>
            <Label color={"$muted"}>Background theme</Label>
            {!!coverUrl && (
              <Image
                style={{ aspectRatio: 3 / 1 }}
                objectFit="cover"
                source={{ uri: coverUrl }}
              />
            )}
            {!!planInfo.backgroundColor && (
              <View
                style={{
                  height: 100,
                  backgroundColor: planInfo.backgroundColor,
                }}
              />
            )}

            {showColors && (
              <Box>
                <XStack
                  width={"100%"}
                  flexWrap="wrap"
                  gap="$small"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <Palette size={"$medium"} />
                  {TagColorsAvailable.map((color) => {
                    const isCurrent = color.color === planInfo.backgroundColor;

                    return (
                      <TouchableOpacity
                        key={color.color}
                        onPress={() => {
                          void businessPlan.updatePlanInfo({
                            coverUrl: null,
                            backgroundColor: color.color,
                          });
                        }}
                      >
                        <Circle
                          key={color.color}
                          backgroundColor={color.color}
                          size="$medium"
                        >
                          {isCurrent && <CheckCircle color={color.textColor} />}
                        </Circle>
                      </TouchableOpacity>
                    );
                  })}
                </XStack>
              </Box>
            )}

            <XStack
              width={"100%"}
              gap={"$medium"}
              marginTop={"$small"}
              alignItems={"center"}
            >
              <View flex={1}>{uploadCoverButton}</View>
              <Label color={"$muted"}>or</Label>

              <View flex={1}>
                <Button
                  icon={showColors ? Check : Palette}
                  onPress={() => {
                    setShowColors(!showColors);
                  }}
                >
                  {showColors ? "Close" : "Color"}
                </Button>
              </View>
            </XStack>
          </Box>

          <Box>
            <Label color={"$muted"}>Description</Label>
            <EditorForm
              id="description"
              value={planInfo.descriptionYooptaEditor}
              onSave={(data) =>
                void businessPlan.updatePlanInfo({
                  descriptionYooptaEditor: data,
                })
              }
            />
          </Box>

          <Box>
            <Label color={"$muted"}>Introduction text</Label>
            <EditorForm
              id="introText"
              value={businessPlan.data.customerInfo.introText}
              onSave={(data) =>
                void businessPlan.updateCustomerInfo({
                  introText: data,
                })
              }
            />
          </Box>
        </YStack>

        <EmojiPicker
          sheetId="emoji"
          open={showEmojiSheet}
          onOpenChange={(open: boolean) => setSetEmojiSheet(open)}
          onSelect={(emoji) => {
            businessPlan.updatePlanInfo({ emoji: emoji.emoji });
            setSetEmojiSheet(false);
          }}
        />
      </>
    );
  },
);
